/* eslint-disable react/require-default-props */
import * as React from 'react';
import { Button, Input, Modal, Select, CheckBox } from '../..';
import { showAlert, useAuth } from '../../../context/authContext';
import useValidator from '../../../hooks/useValidator';
import SampleBarcode from '../../../assets/images/image_sample_barcode.png';
import { Product } from '../../../pages/Inventory/Inventory';
import roundUp from '../../../utils/roundUp';
import apigateway from '../../../utils/apigateway';
import { formatErrors } from '../../../utils/request';

type ModalProps = {
  inventory?: Product;
  open: boolean;
  onCloseModal: () => void;
  onSetLabelPrints: (prints: string) => void;
};

function PrintLabel({
  open,
  inventory,
  onCloseModal,
  onSetLabelPrints,
}: ModalProps) {
  const [{ user }, dispatch] = useAuth();
  const [printSettings, setPrintSettings] = React.useState({
    labelSize: '2.25"x1.25" Rolls',
    quantity: 50,
    showProductName: true,
    showProductPrice: true,
    showProductSku: true,
  });
  const [status, setStatus] = React.useState<
    'IDLE' | 'LOADING' | 'RESOLVED' | 'REJECTED'
  >('IDLE');
  const [validator, showValidationMessage] = useValidator({});

  const currencySymbol = user?.defaultStore?.country?.currencySymbol || '₵';

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;

    setPrintSettings(fndSettings => ({
      ...fndSettings,
      [name]: value,
    }));
  };

  const handleSendPrintRequest = async () => {
    if (!validator.allValid()) {
      showValidationMessage(true);
      return;
    }

    if (!user || !inventory) return;

    setStatus('LOADING');

    const storeId = user.defaultStore.id;

    const payload = {
      ...printSettings,
      productId: inventory.id,
      userId: user.userId,
      storeId,
    };

    try {
      const labels = (await apigateway.post(
        `/print-labels?store=${storeId}`,
        payload,
      )) as { print: { html: string; link: string } };

      setStatus('RESOLVED');

      window.open(labels.print.link, '_blank');

      onCloseModal();
    } catch (err) {
      setStatus('REJECTED');
      const { message } = formatErrors(err);
      showAlert(
        dispatch,
        {
          show: true,
          message:
            message ||
            'Oops. Something unusual happened at our end. Please contact our support team!',
          type: 'ERROR',
        },
        { autoDismiss: true, time: 4000 },
      );
    }
  };

  return (
    <Modal onClose={onCloseModal} show={open}>
      <div className="rounded-md bg-white tablet:w-[900px]">
        <div className="flex flex-col p-5">
          <div className="flex flex-row divide divide-x space-x-5">
            <div className="w-[50%]">
              <h3 className="text-pos-black text-[20px] font-semibold mb-1 inline-flex whitespace-pre">
                Print Labels
              </h3>
              <form className="text-pos-black text-sm mt-2 mb-2 font-normal space-y-4">
                <div>
                  <label htmlFor="labelSize" className="text-xbase block mb-1">
                    Label Size
                  </label>
                  <Select
                    placeholder={{
                      value: '',
                      label: 'Assign a label size',
                    }}
                    value={printSettings.labelSize}
                    options={[
                      {
                        value: '2.25"x1.25" Rolls',
                        label: '2.25"x1.25" Rolls',
                      },
                      {
                        value: '2.0"x1.0" Rolls',
                        label: '2.0"x1.0" Rolls',
                      },
                      {
                        value: '1.5"x1.0" Rolls',
                        label: '1.5"x1.0" Rolls',
                      },
                      {
                        value: '1.25"x0.5" Rolls',
                        label: '1.25"x0.5" Rolls',
                      },
                    ]}
                    name="labelSize"
                    id="labelSize"
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="quantity" className="text-xbase block mb-1">
                    Quantity{' '}
                    <span className="text-xs font-light text-pos-red">*</span>
                  </label>
                  <Input
                    id="quantity"
                    name="quantity"
                    type="number"
                    min={1}
                    placeholder="Enter print quantity"
                    value={printSettings.quantity}
                    onChange={handleInputChange}
                  />
                  {validator.message(
                    'quantity',
                    printSettings.quantity,
                    'numeric|min:1,num|required',
                    {
                      className:
                        'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                    },
                  )}
                </div>

                <div>
                  <div className="border-b border-[#d0d7de] mb-4">
                    <h2 className="text-xbase font-semibold inline-flex items-center">
                      Other Preferences
                    </h2>
                  </div>

                  <div className="space-y-2">
                    <div className="flex flex-row space-x-2 items-center">
                      <CheckBox
                        value={printSettings.showProductName.toString()}
                        name="showProductName"
                        id="showProductName"
                        onChange={event =>
                          setPrintSettings(fndSettings => ({
                            ...fndSettings,
                            showProductName: event.target.checked,
                          }))
                        }
                        checked={printSettings.showProductName}
                        className="cursor-pointer"
                      />
                      <label
                        htmlFor="showProductName"
                        className="text-xbase cursor-pointer"
                      >
                        Show Product Name
                      </label>
                    </div>
                    <div className="flex flex-row space-x-2 items-center">
                      <CheckBox
                        value={printSettings.showProductPrice.toString()}
                        name="showProductPrice"
                        id="showProductPrice"
                        onChange={event =>
                          setPrintSettings(fndSettings => ({
                            ...fndSettings,
                            showProductPrice: event.target.checked,
                          }))
                        }
                        checked={printSettings.showProductPrice}
                        className="cursor-pointer"
                      />
                      <label
                        htmlFor="showProductPrice"
                        className="text-xbase cursor-pointer"
                      >
                        Show Product Price
                      </label>
                    </div>
                    <div className="flex flex-row space-x-2 items-center">
                      <CheckBox
                        value={printSettings.showProductSku.toString()}
                        name="showProductSku"
                        id="showProductSku"
                        onChange={event =>
                          setPrintSettings(fndSettings => ({
                            ...fndSettings,
                            showProductSku: event.target.checked,
                          }))
                        }
                        checked={printSettings.showProductSku}
                        className="cursor-pointer"
                      />
                      <label
                        htmlFor="showProductSku"
                        className="text-xbase cursor-pointer"
                      >
                        Show SKU
                      </label>
                    </div>
                  </div>
                </div>

                <div className="!mt-20 flex flex-col">
                  <span className="text-[13px] block font-medium">
                    Print Total: {printSettings.quantity}
                  </span>
                  <Button
                    label="Print"
                    onClick={() => handleSendPrintRequest()}
                    type="button"
                    disabled={status === 'LOADING'}
                    loading={status === 'LOADING'}
                    className="!bg-pos-green border border-[rgba(27,31,36,0.15)] !min-w-[150px]  h-[40px]
                      items-center justify-center text-white text-xbase
                      shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)] shrink-0 font-medium"
                  />
                </div>
              </form>
            </div>
            <div className="w-[50%] flex flex-col">
              <div className="relative overflow-y-scroll h-full  w-full">
                <div className="absolute pl-16 pr-12">
                  <div className="space-y-5">
                    {new Array(printSettings.quantity)
                      .fill(null)
                      .map((label, index) => (
                        <div
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          className={`border border-pos-gray mx-auto rounded-md px-5 pt-5 ${
                            printSettings.showProductPrice ? 'pb-2' : 'pb-5'
                          }  text-center`}
                        >
                          <img src={SampleBarcode} alt="sample barcode" />
                          <div>
                            {inventory && printSettings.showProductSku && (
                              <span className="mt-1">
                                {' '}
                                {inventory.inventory.sku ||
                                  inventory.inventory.upc}
                              </span>
                            )}

                            {inventory && printSettings.showProductName && (
                              <p className="font-semibold leading-none">
                                {inventory.name}
                              </p>
                            )}

                            {inventory && printSettings.showProductPrice && (
                              <h1 className="font-bold text-3xl leading-[55px] tracking-[0em] inline-flex justify-center">
                                <span className="text-[20px] mr-[2px]">
                                  {currencySymbol}
                                </span>
                                {roundUp(
                                  parseFloat(
                                    inventory.inventory.price.toString(),
                                  ),
                                  2,
                                ).toFixed(2)}
                              </h1>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default PrintLabel;
